import { useCallback, useEffect, useRef, useState } from 'react';
import useUserStore from '@/stores/UserStore';
import { Cross2Icon } from '@radix-ui/react-icons';
import { History, ListChecks, Search } from 'lucide-react';
import { toast } from 'sonner';
import { useDebouncedCallback } from 'use-debounce';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';

import FollowerFilterSheet from './FollowerFilterSheet';

const MAX_SEARCH_LENGTH = 20;
// Regex to match only allowed characters: letters, numbers, basic punctuation, and spaces
const allowedCharactersRegex = /^[a-zA-Z0-9\s.,"!?_-]*$/;

const FollowerFilters = ({
  activeFilter,
  searchQuery,
  isMultiSelectMode,
  filteredFollowersLength,
  totals,
  onFilterChange,
  onSearchChange,
  onMultiSelectToggle,
  isLoading = false,
}) => {
  const isDemoAccount = useUserStore((state) => state.isDemoAccount);
  const [inputText, setInputText] = useState(searchQuery || '');
  const searchInputRef = useRef(null);

  const filterOptions = [
    { value: 'all', label: 'All', count: totals?.all || 0 },
    { value: 'pending', label: 'Pending', count: totals?.pending || 0 },
    { value: 'approved', label: 'Approved', count: totals?.approved || 0 },
    { value: 'rejected', label: 'Rejected', count: totals?.rejected || 0 },
    { value: 'history', label: 'History', count: null, icon: History },
  ];

  const handleDebouncedInput = useDebouncedCallback((value) => {
    onSearchChange(value);
  }, 500);

  const handleSearch = useCallback(
    (value) => {
      if (!allowedCharactersRegex.test(value)) {
        toast.error(
          'Only letters, numbers, and basic punctuation (., !, ?, -, _) are allowed'
        );
        return;
      }

      if (value.length > MAX_SEARCH_LENGTH) {
        toast.error(`Search cannot exceed ${MAX_SEARCH_LENGTH} characters`);
        return;
      }
      setInputText(value);
      handleDebouncedInput(value);
    },
    [handleDebouncedInput]
  );

  const handleSearchClear = useCallback(() => {
    handleSearch('');
  }, [handleSearch]);

  const handleEscapeKeyPress = useCallback((e) => {
    if (e.key === 'Escape') {
      searchInputRef?.current.blur();
    }
  }, []);

  // Sync input text with searchQuery prop when it changes externally
  useEffect(() => {
    setInputText(searchQuery || '');
  }, [searchQuery]);

  return (
    <div
      className={cn(
        'flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between',
        isDemoAccount && 'sm:justify-end'
      )}
    >
      {!isDemoAccount && (
        <div className='flex items-center gap-2'>
          {/* Main Status Tabs */}
          <Tabs
            value={activeFilter}
            onValueChange={onFilterChange}
            className='w-auto'
            disabled={isLoading}
          >
            <TabsList
              className='grid w-full grid-cols-4 sm:w-auto'
              disabled={isLoading}
            >
              {filterOptions.slice(0, 4).map((option) => {
                const IconComponent = option.icon;
                return (
                  <TabsTrigger
                    key={option.value}
                    value={option.value}
                    className='text-xs sm:text-sm'
                    disabled={isLoading}
                  >
                    <div className='flex items-center gap-1'>
                      {IconComponent && <IconComponent className='h-3 w-3' />}
                      <span>{option.label}</span>
                      {option.count !== null && <span>({option.count})</span>}
                    </div>
                  </TabsTrigger>
                );
              })}
            </TabsList>
          </Tabs>

          {/* Separator */}
          <Separator orientation='vertical' className='h-6' />

          {/* History Tab - Separate */}
          <Tabs
            value={activeFilter}
            onValueChange={onFilterChange}
            className='w-auto'
            disabled={isLoading}
          >
            <TabsList className='w-auto' disabled={isLoading}>
              {filterOptions.slice(4).map((option) => {
                const IconComponent = option.icon;
                return (
                  <TabsTrigger
                    key={option.value}
                    value={option.value}
                    className='text-xs sm:text-sm'
                    disabled={isLoading}
                  >
                    <div className='flex items-center gap-1'>
                      {IconComponent && <IconComponent className='h-3 w-3' />}
                      <span>{option.label}</span>
                      {option.count !== null && <span>({option.count})</span>}
                    </div>
                  </TabsTrigger>
                );
              })}
            </TabsList>
          </Tabs>
        </div>
      )}

      <div className='flex items-center gap-3'>
        <div className='relative w-full sm:w-64'>
          <Search className='absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground' />
          <Input
            ref={searchInputRef}
            placeholder='Search by username...'
            value={inputText}
            onChange={(e) => handleSearch(e.target.value)}
            onKeyDown={handleEscapeKeyPress}
            className='pl-10 pr-10'
          />
          {inputText && (
            <Button
              variant='ghost'
              size='icon'
              className='absolute right-1 top-1 h-8 w-8 rounded-full'
              onClick={handleSearchClear}
            >
              <Cross2Icon />
            </Button>
          )}
        </div>

        <div className='flex items-center gap-2'>
          <FollowerFilterSheet />
          {filteredFollowersLength > 0 && !isDemoAccount && (
            <Button
              variant={isMultiSelectMode ? 'default' : 'outline'}
              size='sm'
              onClick={onMultiSelectToggle}
              className='h-8 px-3'
            >
              <ListChecks className='mr-2 h-4 w-4' />
              {isMultiSelectMode ? 'Single Select Mode' : 'Multi Select Mode'}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default FollowerFilters;
